import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Download,
  Description,
  GetApp,
  Visibility,
  CheckCircle,
  Label,
  Assignment,
  QrCode,
  LocalShipping,
  Inventory,
  Clear
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const CartonLabels = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [importFile, setImportFile] = useState(null);
  const [importFilePath, setImportFilePath] = useState('');
  const [labelType, setLabelType] = useState('Amazon');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processId, setProcessId] = useState(null);

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);
  const fileInputRef = useRef(null);

  // Helper function to add logs to activity context
  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) {
          clearIntervalRef(processIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  const labelTypes = [
    { id: 'Amazon', label: 'Amazon', icon: <LocalShipping /> },
    { id: 'WayfairEUV1', label: 'Wayfair EU Version 1', icon: <Inventory /> },
    { id: 'WayfairEUV2', label: 'Wayfair EU Version 2', icon: <Inventory /> },
    { id: 'WayfairUS', label: 'Wayfair US', icon: <LocalShipping /> },
    { id: 'WayfairUSV2', label: 'Wayfair US V2', icon: <LocalShipping /> },
    { id: 'WayfairDI', label: 'Wayfair DI', icon: <Inventory /> },
    { id: 'WayfairAsia', label: 'Wayfair Asia', icon: <Inventory /> }
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a valid PDF file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      setError('File size must be less than 100MB');
      return;
    }

    setImportFile(file);
    setImportFilePath(file.name);
    setError('');
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  // Fetch status from server and update activity log
  const fetchStatus = async (currentProcessId, activityProcessId) => {
    try {
      const response = await apiCall(API_ENDPOINTS.CARTON_LABELS_STATUS(currentProcessId), {
        method: 'GET',
      });

      // Update activity log with server logs
      if (response.logs && response.logs.length > 0) {
        setLogs(activityProcessId, response.logs);
      }

      // Update progress
      if (response.progressPercentage !== undefined) {
        updateActivityProcess(activityProcessId, { progressPercentage: response.progressPercentage });
      }

      // Check if completed or errored
      if (response.status === 'completed') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        // Auto-download if not already triggered
        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(currentProcessId, activityProcessId, response.fileName);
        }

        setSuccess('Processing completed! Carton labels generated and downloaded successfully.');
        setResults({
          success: true,
          fileName: response.fileName,
          labelType: labelType
        });
        completeActivityProcess(activityProcessId, true);
        setProcessing(false);
      } else if (response.status === 'error') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        setError('Processing failed. Please check the activity log for details.');
        completeActivityProcess(activityProcessId, false);
        setProcessing(false);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  // Download file from server
  const downloadFile = async (currentProcessId, activityProcessId, fileName) => {
    try {
      const response = await fetch(API_ENDPOINTS.CARTON_LABELS_DOWNLOAD(currentProcessId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'Carton_Labels.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addLog(activityProcessId, `✓ PDF downloaded: ${fileName}`);
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(activityProcessId, `✗ Error downloading file: ${err.message}`);
    }
  };

  const handleProcessLabels = async () => {
    if (!importFile) {
      setError('Please select a PDF file before processing.');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);
    downloadTriggeredRef.current = false;

    // Generate a unique process ID for activity log
    const activityProcessId = `carton-labels-${Date.now()}`;
    const labelTypeDisplay = labelTypes.find(t => t.id === labelType)?.label || labelType;

    // Add to global activity log
    addActivityProcess(activityProcessId, `EU-UCC PDF Modifier (${labelTypeDisplay})`, 'EU-UCC PDF Modifier');
    addLog(activityProcessId, `Starting carton label processing...`);
    addLog(activityProcessId, `Label Type: ${labelTypeDisplay}`);
    addLog(activityProcessId, `File: ${importFile.name}`);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('labelType', labelType);

      addLog(activityProcessId, `⏳ Uploading file to server...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 10 });

      // Make API call to start processing
      const response = await apiCall(API_ENDPOINTS.CARTON_LABELS_PROCESS, {
        method: 'POST',
        body: formData,
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        setProcessId(serverProcessId);
        processIdRef.current = serverProcessId;

        addLog(activityProcessId, `⏳ Processing started on server...`);

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus(serverProcessId, activityProcessId);
        }, 1000);
        setIntervalRef(activityProcessId, intervalRef.current);
      } else {
        throw new Error('No process ID returned from server');
      }

    } catch (err) {
      addLog(activityProcessId, `✗ Error: ${err.message}`);
      completeActivityProcess(activityProcessId, false);
      setError(`Processing failed: ${err.message}`);
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setImportFile(null);
    setImportFilePath('');
    setError('');
    setSuccess('');
    setResults(null);
  };

  const clearFile = () => {
    setImportFile(null);
    setImportFilePath('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="EU-UCC PDF Modifier" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          EU-UCC PDF MODIFIER - ITEM
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Label Processing
              </Typography>

              {/* Label Type Selection */}
              <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
                <CardContent>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Label sx={{ color: TOOL_COLORS.PDF.primary }} />
                        Select Label Type
                      </Box>
                    </FormLabel>
                    <RadioGroup
                      value={labelType}
                      onChange={(e) => setLabelType(e.target.value)}
                      sx={{ mt: 1 }}
                    >
                      {labelTypes.map((type) => (
                        <FormControlLabel
                          key={type.id}
                          value={type.id}
                          control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {React.cloneElement(type.icon, { sx: { color: '#DC143C' } })}
                              <span>{type.label}</span>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>

              {/* File Upload */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Import PDF File
                    </Typography>
                    {importFile && (
                      <Tooltip title="File selected">
                        <Visibility sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Hidden File Input */}
                  <input
                    type="file"
                    hidden
                    accept=".pdf"
                    onChange={handleFileUpload}
                    id="file-input"
                    ref={fileInputRef}
                  />

                  {/* Drag & Drop Zone - Clickable */}
                  <Box
                    component="label"
                    htmlFor="file-input"
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    sx={{
                      border: `2px dashed ${isDragging ? TOOL_COLORS.PDF.primary : importFile ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragging
                        ? TOOL_COLORS.PDF.light
                        : importFile
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'block',
                      mb: 2,
                      '&:hover': {
                        borderColor: importFile ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                        bgcolor: importFile
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.PDF.light,
                      }
                    }}
                  >
                    {importFile ? (
                      <Box>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to replace
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.PDF.primary : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {isDragging ? 'Drop PDF file here' : 'Drag & drop PDF file here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Supported format: PDF (max 100MB)
                        </Typography>
                      </>
                    )}
                  </Box>
                  {importFile && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {importFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({(importFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          clearFile();
                        }}
                        disabled={processing}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleProcessLabels}
                  disabled={!importFile || processing}
                  sx={{
                    bgcolor: TOOL_COLORS.PDF.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                    px: 4
                  }}
                >
                  {processing ? 'Processing Labels...' : 'Process Labels'}
                </Button>

                {(importFile || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={processing}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Download Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    Processing {labelTypes.find(t => t.id === labelType)?.label} labels...
                  </Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.PDF.primary } }} />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Label sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${labelType} Labels Processed`}
                        secondary={`Item IDs added to carton labels`}
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <QrCode sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="UPC Code Lookup"
                        secondary="Automatic Item ID matching via UPC codes"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Assignment sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`File: ${results.fileName}`}
                        secondary="Successfully processed and downloaded"
                        primaryTypographyProps={{ color: theme.palette.text.primary }}
                        secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              {/* Download Section - File auto-downloads after generation */}
              {results && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Labels have been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Current Configuration */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                Current Configuration:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                <Chip
                  label={`Type: ${labelTypes.find(t => t.id === labelType)?.label}`}
                  size="small"
                  sx={{ borderColor: TOOL_COLORS.PDF.primary, color: TOOL_COLORS.PDF.primary }}
                  variant="outlined"
                />
                {importFile && (
                  <Chip
                    label={`File: ${importFile.name.substring(0, 20)}...`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                Instructions:
              </Typography>
              <Typography variant="body2" component="div" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>1.</Box> Select your label type (Amazon or Wayfair)<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>2.</Box> Upload your carton label PDF file<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>3.</Box> Process labels to add Item IDs<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>4.</Box> Download the processed PDF<br /><br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>Process:</Box> The system extracts UPC codes from the PDF and automatically looks up matching Item IDs from the database, then adds them to the carton labels.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CartonLabels;
